import React, { useEffect, useState } from "react";
import { Container, Form, Row, Col, Button } from "react-bootstrap";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import moment from "moment";
import reportApi from "../../services/reportapi";
import Loader from "../loader/loader";
import DynamicBreadcrumb from "../header/breadcrumb";
import branchApi from "../../services/branchapi";

const DeliveryPerformance = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        from_date: moment().format("YYYY-MM-DD"),
        to_date: moment().format("YYYY-MM-DD"),
        branch_id: ""
    });
    const [branches, setBranches] = useState([]);
    const [loaderVisible, setLoaderVisible] = useState(false);

    const updateFilters = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const fetchDeliveryPerformance = async () => {
        setLoaderVisible(true);
        setLoading(true);
        try {
            const response = await reportApi.getDeliveryPerformance(filters);
            const formattedData = (response.data || []).map((row, index) => {
                // Format the date range
                let formattedDate = row.date;
                const [startDateStr, endDateStr] = row.date.split(' - ');

                if (startDateStr && endDateStr) {
                    const start = moment(startDateStr).format('DD-MMM-YYYY');
                    const end = moment(endDateStr).format('DD-MMM-YYYY');
                    formattedDate = `${start} to ${end}`;
                }

                return {
                    id: index + 1,
                    ...row,
                    date: formattedDate
                };
            });
            setRows(formattedData);
        } catch (err) {
            console.error("Error fetching delivery performance:", err);
        } finally {
            setLoaderVisible(false);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDeliveryPerformance();
    }, []);

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const branchData = await branchApi.getAllBranches({
                    limit: 100,
                    page: 0,
                    status: 1,
                    sortModel: [{ field: "branchName", sort: "asc" }],
                });
                setBranches(branchData.data.result || []);
            } catch (error) {
                console.error("Error fetching branches:", error);
            }
        };
        fetchBranches();
    }, []);

    const columns = [
        { field: "branch_name", headerName: "Branch", flex: 1 },
        { field: "date", headerName: "Date", flex: 1 },
        { field: "total", headerName: "Total Delivered", flex: 1 },
        { field: "ontime", headerName: "On-Time", flex: 1 },
        { field: "earlier", headerName: "Early", flex: 1 },
        { field: "delayed", headerName: "Delayed", flex: 1 },
    ];

    const pathArray = [{ name: "Delivery Performance", url: null }];

    return (
        <Container className="mt-4 mb-4">
            <DynamicBreadcrumb pathArray={pathArray} />
            <h5>Delivery Performance Report</h5>
            <Form className="mb-3">
                <Row>
                    <Col md={3}>
                        <Form.Label>Branch</Form.Label>
                        <Form.Select
                            value={filters.branch_id}
                            onChange={(e) => updateFilters("branch_id", e.target.value)}
                        >
                            <option value="">All Branches</option>
                            {branches.map(branch => (
                                <option key={branch.branchId} value={branch.branchId}>
                                    {branch.branchName}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col md={3}>
                        <Form.Label>From Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={filters.from_date}
                            onChange={(e) => updateFilters("from_date", e.target.value)}
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Label>To Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={filters.to_date}
                            onChange={(e) => updateFilters("to_date", e.target.value)}
                        />
                    </Col>
                    <Col md={3} className="d-flex align-items-end">
                        <Button variant="primary" onClick={fetchDeliveryPerformance}>
                            Filter
                        </Button>
                    </Col>
                </Row>
            </Form>

            {loaderVisible && <Loader />}

            <Box>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowHeight={() => "auto"}
                    loading={loading}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            csvOptions: {
                                fileName: `DeliveryPerformance_${moment().format("YYYYMMDD_HHmmss")}`,
                                utf8WithBom: true
                            },
                            printOptions: { disableToolbarButton: true },
                        },
                    }}
                    sx={{
                        [`& .${gridClasses.cell}`]: { py: 1 },
                    }}
                />
            </Box>
        </Container>
    );
};

export default DeliveryPerformance;
